/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getPayloadObject, getTelemetryAttributes, IPayLoad } from '@msdyn365-commerce-modules/utilities';
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { ProductRefinerValue } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
// import { get } from 'lodash';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IChoiceSummaryProps } from './choice-summary.props';
// import { isMatchingRefinementCriterion, isRangeType } from './utilities';
import { isMatchingRefinementCriterion, ProductRefinerValueDataTypeValue } from './utilities';
import get from 'lodash/get';
import { getIsSearchPage } from '../dobbies-search-result-container.view';

interface IRefinerMap {
    key: string;
    value: ProductRefinerValue;
}

/**
 * ChoiceSummary component
 */
@observer
export default class ChoiceSummary extends React.Component<IChoiceSummaryProps> {
    private closeButtonGlyph: string = 'msi-close-btn';
    private payLoad: IPayLoad;

    @computed get selectedRefinersMap(): IRefinerMap[] {
        const { selectedChoices } = this.props;
        return selectedChoices.map((selectedRefiner: ProductRefinerValue) => {
            return {
                key: this._getKeyForRefinerValue(selectedRefiner),
                value: selectedRefiner
            } as IRefinerMap;
        });
    }

    constructor(props: Readonly<IChoiceSummaryProps>) {
        super(props);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
    }

    public render(): JSX.Element {
        const { clearAllText, label, classNames, choiceAriaLabel, FilterActiveText, totalProductCount } = this.props;
        const isSearchPage = getIsSearchPage(this.props.context);
        const items = this.selectedRefinersMap;
        this.payLoad.contentAction.etext = clearAllText;
        const clearAllAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        return (
            <div className='msc-choice-summary'>
                {!isSearchPage && totalProductCount !== undefined && (
                    <span
                        className={classnames('msc-choice-summary__count', {
                            'msc-choice-summary__count-active':
                                items.length > 0 || (this.props.selectedSortOption && this.props.selectedSortOption?.value !== 'Relevance')
                        })}
                    >
                        {totalProductCount} {totalProductCount === 1 ? 'result' : FilterActiveText}
                    </span>
                )}
                {items.length > 0 && label && <span className='msc-choice-summary__label'>{label}</span>}
                <ul className={classnames(classNames, 'msc-choice-summary__list', 'list-unstyled')}>
                    {this.props.selectedSortOption && this.props.selectedSortOption.value !== 'Relevance' && (
                        <li className='msc-choice-summary__list-item' key={this.props.selectedSortOption.value}>
                            <a
                                className='msc-choice-summary__item'
                                aria-label={`${this.props.selectedSortOption.value} ${choiceAriaLabel}`}
                                onClick={e => this._onClick(e, true)}
                            >
                                Sort: {this.props.selectedSortOption.value.replace(':', '').toLowerCase()}
                                <span className={`${this.closeButtonGlyph} msc-choice-summary__glyph`} />
                            </a>
                        </li>
                    )}
                    {items.map((item: IRefinerMap, index: number) => {
                        const listItemProps = {
                            'aria-posinset': index,
                            'aria-setsize': items.length
                        };

                        this.payLoad.contentAction.etext = item.key;
                        const attribute = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);

                        return (
                            <li className='msc-choice-summary__list-item' key={item.key} {...listItemProps}>
                                <a
                                    className='msc-choice-summary__item'
                                    href={this.props.urlBuilder(item.value, false)}
                                    aria-label={`${item.key} ${choiceAriaLabel}`}
                                    onClick={this._onClick}
                                    {...attribute}
                                >
                                    {item.key}
                                    <span className={`${this.closeButtonGlyph} msc-choice-summary__glyph`} />
                                </a>
                            </li>
                        );
                    })}
                    <li>
                        {(items.length > 0 || (this.props.selectedSortOption && this.props.selectedSortOption.value !== 'Relevance')) &&
                            clearAllText && (
                                <a
                                    href={this.props.urlBuilder({}, true)}
                                    className={'msc-choice-summary__clear-all'}
                                    {...clearAllAttributes}
                                    onClick={this._onClick}
                                >
                                    {clearAllText}
                                </a>
                            )}
                    </li>
                </ul>
            </div>
        );
    }

    private _getKeyForRefinerValue(productRefinerValue: ProductRefinerValue): string {
        const { choiceFormat, choiceRangeValueFormat, refinerHierarchy, telemetry } = this.props;
        const overallFormat = choiceFormat || '{1}';
        const rangeFormat = choiceRangeValueFormat;
        let refinerName = '';
        if (refinerHierarchy && refinerHierarchy.find) {
            const parent = refinerHierarchy.find(
                (hierarchy: IProductRefinerHierarchy) =>
                    !!hierarchy.Values.find((value: ProductRefinerValue) => isMatchingRefinementCriterion(value, productRefinerValue))
            );

            if (!parent) {
                telemetry.warning('[choice-summary] could not find parent of selected refiner value');
            } else {
                refinerName = parent.KeyName || '';
            }
        }

        let refinerValueName: string;
        const leftRefinerPrice = this._formatPrice(productRefinerValue.LeftValueBoundString, this.props.context?.request.channel?.Currency);
        const rightRefinerPrice = this._formatPrice(
            productRefinerValue.RightValueBoundString,
            this.props.context?.request.channel?.Currency
        );
        switch (productRefinerValue.DataTypeValue) {
            case ProductRefinerValueDataTypeValue.Range:
            case ProductRefinerValueDataTypeValue.RangeInput:
                refinerValueName = rangeFormat.replace('{0}', leftRefinerPrice).replace('{1}', rightRefinerPrice);
                break;
            default:
                refinerValueName = productRefinerValue.LeftValueBoundLocalizedString || productRefinerValue.LeftValueBoundString || '';
        }

        return overallFormat.replace('{0}', refinerName).replace('{1}', refinerValueName);
    }

    private _formatPrice(amount: string | undefined, currency: string | undefined): string {
        if (!amount || !currency) {
            this.props.telemetry.trace('[choice-summary] could not format price');
            return amount || '';
        }
        const priceAmount = (amount && Number(amount)) || 0;
        const locale = get(this.props, 'context.request.locale', 'en-US');
        let result: string;

        try {
            result = new Intl.NumberFormat(locale, {
                style: 'currency',
                currencyDisplay: 'symbol',
                currency: currency,
                minimumFractionDigits: 0
            }).format(priceAmount);
        } catch (e) {
            result = `${priceAmount} ${currency}`;
            this.props.telemetry.warning(`Failed to format price for ${result}: ${e}`);
        }

        return result;
    }

    private _onClick = (e: React.MouseEvent<HTMLAnchorElement>, isSortOptionClicked?: boolean): void => {
        e.preventDefault();
        e.stopPropagation();

        if (isSortOptionClicked && this.props.onSelectedSortOptionClicked) {
            this.props.onSelectedSortOptionClicked();
            return;
        }

        const target = e.currentTarget as HTMLElement;
        const clearAll = target.getAttribute('class')!.indexOf('choice-summary__clear-all') > -1;
        const selectedRefiner = clearAll ? undefined : this._getSelectedRefinerChoice(target);

        if (this.props.onChoiceClicked) {
            this.props.onChoiceClicked({
                clearAll: clearAll,
                itemClicked: target,
                choiceClicked: selectedRefiner,
                nextItemToFocus: target.nextSibling as HTMLElement
            });
        }
    };

    private _getSelectedRefinerChoice(itemClicked: HTMLElement): ProductRefinerValue | undefined {
        const result = this.selectedRefinersMap.find(selected => (itemClicked.innerText && itemClicked.innerText.trim()) === selected.key);
        return (result && result.value) || undefined;
    }
}
